import { useNavigate } from "react-router-dom";
import { ArrowRight, Play } from "lucide-react";

/* ── GENRYZ / Terminal ─────────────────────────────────────────────────────
 * Specialty design direction: a terminal/OS aesthetic that leans into the
 * "OS" in the brand name. White page (locked) + dark terminal-window sections,
 * monospace type, a phosphor-green spark, shell prompts, a boot-sequence hero,
 * log-style testimonials and an IRC-style community. Self-contained — renders
 * its own chrome and footer instead of the shared SiteHeader/SiteFooter.
 */

const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

const STATS = [
  { v: "10,000+", c: "// students trained" },
  { v: "25+", c: "// skill modules" },
  { v: "2M+", c: "// content reach" },
  { v: "5,000+", c: "// learners online" },
];

const WINS = [
  "Ankit → ₹1,45,000/mo · affiliate",
  "Sakshi → ₹80,000/mo · instagram",
  "Harsh → ₹1,10,000/mo · youtube",
  "Karan → sold 3 no-code apps",
  "Riya → 4 freelance clients landed",
  "Rohit → ₹1,35,000/mo · ai automation",
  "Neha → email list = top revenue channel",
];

const SKILL_PATHS = [
  { dir: "creator-economy", name: "Creator Economy", skills: ["instagram_growth", "youtube_monetization", "video_editing", "personal_branding"] },
  { dir: "marketing-sales", name: "Marketing & Sales", skills: ["affiliate_marketing", "meta_ads", "lead_generation", "email_marketing"] },
  { dir: "ai-future-skills", name: "AI & Future Skills", skills: ["ai_automation", "prompt_engineering", "chatgpt_mastery", "no_code_tools"] },
  { dir: "lifestyle-creator", name: "Lifestyle & Creator", skills: ["makeup_mastery", "gaming_content", "travel_vlogging", "photography"] },
];

const HOW = [
  { n: "01", cmd: "learn", desc: "clone the playbooks · 25+ premium courses" },
  { n: "02", cmd: "build", desc: "ship real projects · compile a portfolio" },
  { n: "03", cmd: "ship", desc: "launch your brand · push to production" },
  { n: "04", cmd: "earn", desc: "monetize · stream income · scale ₹" },
];

const TESTIMONIALS = [
  { user: "ankit", handle: "@Awesm.Raj", out: "₹1,45,000/mo", msg: "affiliate marketing gave me a system, not theory. hit target in 5 months." },
  { user: "harsh", handle: "@YouTubeBiarmy", out: "₹1,10,000/mo", msg: "finally understand the algorithm — the income shows it." },
  { user: "karan", handle: "@NoCodeTools", out: "₹1,20,000/mo", msg: "built & sold 3 no-code apps. never thought i'd call myself a developer." },
  { user: "riya", handle: "@Freelancing", out: "₹95,000/mo", msg: "learned to pitch and price. steady clients every month now." },
];

const CHANNELS = ["#announcements", "#wins", "#resources", "#events", "#genryz-chat", "#chill-zone"];

const BOOT_LINES = [
  { p: "$", t: "genryz init --you", c: "text-[#C9D1D9]" },
  { p: ">", t: "booting genryz...", c: "text-[#8B949E]" },
  { p: ">", t: "loading skills........ 25 modules", c: "text-[#8B949E]", ok: "✓" },
  { p: ">", t: "connecting community.. 5,000+ learners", c: "text-[#8B949E]", ok: "✓" },
  { p: ">", t: "calibrating income..... ₹ stream ready", c: "text-[#8B949E]", ok: "✓" },
];

// Dark terminal window with chrome (traffic dots + title bar).
function TermWindow({ title, children, className = "", bodyClass = "" }) {
  return (
    <div className={`relative rounded-xl border border-[#30363D] bg-[#0D1117] shadow-[0_24px_60px_-30px_rgba(0,0,0,0.45)] overflow-hidden ${className}`}>
      <div className="flex items-center gap-2 border-b border-[#30363D] bg-[#161B22] px-4 py-2.5">
        <span className="w-3 h-3 rounded-full bg-[#F85149]" />
        <span className="w-3 h-3 rounded-full bg-[#D29922]" />
        <span className="w-3 h-3 rounded-full bg-[#3FB950]" />
        <span className="ml-2 text-[11px] text-[#8B949E]" style={{ fontFamily: MONO }}>{title}</span>
      </div>
      <div className={`p-5 ${bodyClass}`} style={{ fontFamily: MONO }}>{children}</div>
    </div>
  );
}

export default function LandingTerminal() {
  const navigate = useNavigate();
  const ticker = [...WINS, ...WINS];

  return (
    <div className="min-h-screen text-[#0D1117] overflow-x-hidden" style={{ background: "#FFFFFF" }}>
      <style>{`
        @keyframes termBlink { 0%,49% { opacity: 1 } 50%,100% { opacity: 0 } }
        @keyframes termFade { from { opacity: 0; transform: translateY(4px) } to { opacity: 1; transform: none } }
        @keyframes termTicker { from { transform: translateX(0) } to { transform: translateX(-50%) } }
        @keyframes termPulse { 0%,100% { opacity: 1 } 50% { opacity: .35 } }
        .term-cursor { display:inline-block; width:.6em; height:1.05em; background:#3FB950; margin-left:2px; vertical-align:-2px; animation: termBlink 1.05s steps(1) infinite; }
        .term-bootline { opacity:0; animation: termFade .45s ease forwards; }
        .term-ticker { display:inline-flex; white-space:nowrap; animation: termTicker 26s linear infinite; }
        .term-grid { background-image: radial-gradient(rgba(13,17,23,0.05) 1px, transparent 1px); background-size: 22px 22px; }
        .term-dot { animation: termPulse 1.8s ease-in-out infinite; }
        .term-scan { background: repeating-linear-gradient(to bottom, transparent 0, transparent 2px, rgba(255,255,255,0.025) 2px, rgba(255,255,255,0.025) 3px); }
      `}</style>

      {/* ── Status bar ── */}
      <div className="sticky top-0 z-50 border-b border-[#30363D] bg-[#0D1117] text-[#8B949E]">
        <div className="max-w-6xl mx-auto px-5 h-9 flex items-center justify-between text-[11px]" style={{ fontFamily: MONO }}>
          <div className="flex items-center gap-4">
            <span className="text-[#3FB950] font-bold">&gt; genryz</span>
            <span className="hidden sm:inline">v1.0.0</span>
            <span className="hidden md:inline">· uptime 100%</span>
            <span className="hidden md:inline">· <span className="text-[#3FB950] term-dot">●</span> 5,000 learners online</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline">⌘K</span>
            <span className="text-[#D29922]">[ live ]</span>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="term-grid relative">
        <div className="max-w-6xl mx-auto px-5 pt-14 pb-20 lg:pt-20 lg:pb-28 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <p className="text-[#6E7681] text-xs mb-5" style={{ fontFamily: MONO }}>// initialize your income stream</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight" style={{ fontFamily: MONO }}>
              Build income<br />like it's <span className="text-[#1F9D46]">software</span>.
            </h1>
            <p className="text-[#4A5360] text-base mt-6 max-w-md leading-relaxed">
              GENRYZ is the operating system for GenZ income. Boot up, load a skill path,
              ship projects, and stream revenue — all in one terminal.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <button onClick={() => navigate("/login")}
                className="group inline-flex items-center gap-2 rounded-lg bg-[#0D1117] text-[#3FB950] px-5 py-3 text-sm font-bold transition-all hover:brightness-125 active:scale-95"
                style={{ fontFamily: MONO }}>
                <span className="text-[#3FB950]">$</span> start_learning
                <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
              </button>
              <button onClick={() => navigate("/courses")}
                className="inline-flex items-center gap-2 rounded-lg border border-[#0D1117]/15 px-5 py-3 text-sm font-bold text-[#0D1117] transition-all hover:border-[#1F9D46] hover:text-[#1F9D46] active:scale-95"
                style={{ fontFamily: MONO }}>
                <Play size={14} /> watch_demo
              </button>
            </div>
            <p className="text-[#6E7681] text-[11px] mt-6" style={{ fontFamily: MONO }}>
              # no credit card required · lifetime access · community-driven
            </p>
          </div>

          {/* Boot terminal */}
          <TermWindow title="~/genryz — bash" className="relative">
            <div className="absolute inset-0 term-scan pointer-events-none opacity-60" />
            <div className="relative text-[13px] leading-7">
              {BOOT_LINES.map((l, i) => (
                <div key={i} className="term-bootline" style={{ animationDelay: `${0.15 + i * 0.28}s` }}>
                  <span className="text-[#3FB950]">{l.p}</span>{" "}
                  <span className={l.c}>{l.t}</span>
                  {l.ok && <span className="text-[#3FB950]"> {l.ok}</span>}
                </div>
              ))}
              <div className="term-bootline mt-1" style={{ animationDelay: `${0.15 + BOOT_LINES.length * 0.28}s` }}>
                <span className="text-[#D29922]">█</span>{" "}
                <span className="text-[#C9D1D9]">genryz ready — type </span>
                <span className="text-[#58A6FF]">'start'</span>
                <span className="text-[#C9D1D9]"> to begin</span>
                <span className="term-cursor" />
              </div>
            </div>
          </TermWindow>
        </div>
      </section>

      {/* ── Stats ── */}
      <section className="border-y border-[#0D1117]/8 bg-[#FAFBFC]">
        <div className="max-w-6xl mx-auto px-5 py-10 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {STATS.map((s, i) => (
            <div key={i} className="flex items-start gap-3">
              <span className="text-[#1F9D46] text-lg leading-none mt-1" style={{ fontFamily: MONO }}>▍</span>
              <div>
                <p className="text-2xl lg:text-3xl font-extrabold tracking-tight" style={{ fontFamily: MONO }}>{s.v}</p>
                <p className="text-[#6E7681] text-[11px] mt-1" style={{ fontFamily: MONO }}>{s.c}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Wins ticker ── */}
      <section className="max-w-6xl mx-auto px-5 py-14">
        <p className="text-[#6E7681] text-xs mb-4" style={{ fontFamily: MONO }}>// tail -f wins.log</p>
        <TermWindow title="~/genryz/wins — tail -f">
          <div className="overflow-hidden">
            <div className="term-ticker text-[13px]">
              {[...ticker, ...ticker].map((w, i) => (
                <span key={i} className="inline-flex items-center gap-2 px-6">
                  <span className="text-[#3FB950]">»</span>
                  <span className="text-[#C9D1D9]">{w}</span>
                  <span className="text-[#30363D]">|</span>
                </span>
              ))}
            </div>
          </div>
        </TermWindow>
      </section>

      {/* ── Skill paths ── */}
      <section id="skill-paths" className="max-w-6xl mx-auto px-5 py-14">
        <div className="mb-10">
          <p className="text-[#6E7681] text-xs" style={{ fontFamily: MONO }}>// ls ~/skills</p>
          <h2 className="text-3xl lg:text-4xl font-extrabold mt-2 tracking-tight" style={{ fontFamily: MONO }}>
            pick a <span className="text-[#1F9D46]">skill path</span>
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SKILL_PATHS.map((p) => (
            <div key={p.dir}
              className="group rounded-xl border border-[#0D1117]/10 bg-white p-5 transition-all hover:-translate-y-1 hover:border-[#1F9D46]/50 hover:shadow-[0_18px_40px_-22px_rgba(31,157,70,0.4)]">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[#1F9D46] text-sm font-bold" style={{ fontFamily: MONO }}>~/skills</span>
                <span className="text-[#6E7681] text-[11px]" style={{ fontFamily: MONO }}>dir</span>
              </div>
              <h3 className="text-base font-extrabold mb-3" style={{ fontFamily: MONO }}>{p.name}</h3>
              <ul className="space-y-1.5 mb-5" style={{ fontFamily: MONO }}>
                {p.skills.map((s) => (
                  <li key={s} className="flex items-center gap-2 text-[12px] text-[#4A5360]">
                    <span className="text-[#1F9D46]">·</span> {s}
                  </li>
                ))}
              </ul>
              <button onClick={() => navigate("/courses")}
                className="w-full rounded-lg border border-[#0D1117]/12 py-2.5 text-[12px] font-bold text-[#0D1117] transition-all group-hover:bg-[#0D1117] group-hover:text-[#3FB950] group-hover:border-[#0D1117]"
                style={{ fontFamily: MONO }}>
                $ run →
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="bg-[#FAFBFC] border-y border-[#0D1117]/8">
        <div className="max-w-4xl mx-auto px-5 py-16">
          <p className="text-[#6E7681] text-xs mb-2" style={{ fontFamily: MONO }}>// genryz --run pipeline</p>
          <h2 className="text-3xl lg:text-4xl font-extrabold mb-10 tracking-tight" style={{ fontFamily: MONO }}>
            the <span className="text-[#1F9D46]">pipeline</span>
          </h2>
          <div className="space-y-4" style={{ fontFamily: MONO }}>
            {HOW.map((h) => (
              <div key={h.n} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-5 rounded-lg border border-[#0D1117]/8 bg-white px-5 py-4 transition-all hover:border-[#1F9D46]/40">
                <span className="text-[#6E7681] text-sm w-8">{h.n}</span>
                <span className="text-[#1F9D46] text-base font-bold w-24">$ {h.cmd}</span>
                <span className="text-[#4A5360] text-sm flex-1">{h.desc}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials log ── */}
      <section className="max-w-5xl mx-auto px-5 py-16">
        <p className="text-[#6E7681] text-xs mb-4" style={{ fontFamily: MONO }}>// cat testimonials.log</p>
        <TermWindow title="~/genryz — cat testimonials.log">
          <div className="space-y-5 text-[13px] leading-6">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="border-b border-[#30363D]/60 last:border-0 pb-4 last:pb-0">
                <div className="flex items-center gap-2">
                  <span className="text-[#3FB950]">»</span>
                  <span className="text-[#58A6FF]">{t.user}</span>
                  <span className="text-[#8B949E]">{t.handle}</span>
                </div>
                <div className="ml-4 mt-1">
                  <span className="text-[#8B949E]">out: </span>
                  <span className="text-[#D29922] font-bold">{t.out}</span>
                </div>
                <p className="ml-4 mt-1 text-[#C9D1D9]">"{t.msg}"</p>
              </div>
            ))}
          </div>
        </TermWindow>
      </section>

      {/* ── Community (IRC) ── */}
      <section id="community" className="max-w-5xl mx-auto px-5 py-16">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-[#6E7681] text-xs mb-2" style={{ fontFamily: MONO }}>// /join #genryz</p>
            <h2 className="text-3xl lg:text-4xl font-extrabold tracking-tight" style={{ fontFamily: MONO }}>
              5,000+ learners<br /><span className="text-[#1F9D46]">online</span>
            </h2>
            <p className="text-[#4A5360] mt-5 max-w-md leading-relaxed">
              A live community running 24/7. Share wins, get unblocked, find accountability —
              it runs like a channel, not a comment section.
            </p>
            <button onClick={() => window.open("https://chat.whatsapp.com/REPLACE_WITH_INVITE", "_blank")}
              className="mt-7 inline-flex items-center gap-2 rounded-lg bg-[#0D1117] text-[#3FB950] px-5 py-3 text-sm font-bold transition-all hover:brightness-125 active:scale-95"
              style={{ fontFamily: MONO }}>
              <span className="text-[#3FB950]">&gt;</span> /join #genryz-chat
              <ArrowRight size={15} />
            </button>
          </div>
          <TermWindow title="#genryz — irc.genryz.in">
            <div className="flex flex-wrap gap-x-4 gap-y-2 text-[13px] mb-5">
              {CHANNELS.map((c) => (
                <span key={c} className="text-[#3FB950] hover:text-[#58A6FF] cursor-pointer">{c}</span>
              ))}
            </div>
            <div className="space-y-1.5 text-[12px] text-[#8B949E]">
              <div><span className="text-[#58A6FF]">riya</span> joined #genryz-chat</div>
              <div><span className="text-[#58A6FF]">karan</span> &gt; shipped 3rd no-code app 🎉</div>
              <div><span className="text-[#58A6FF]">ankit</span> &gt; hit ₹1.4L/mo milestone</div>
              <div className="text-[#3FB950]">* new win logged to wins.log</div>
            </div>
            <div className="mt-4 pt-3 border-t border-[#30363D]/60 flex items-center gap-2 text-[13px]">
              <span className="text-[#3FB950]">&gt;</span>
              <span className="text-[#C9D1D9]">type a message</span>
              <span className="term-cursor" />
            </div>
          </TermWindow>
        </div>
      </section>

      {/* ── Pricing CTA ── */}
      <section id="pricing" className="max-w-4xl mx-auto px-5 py-16">
        <TermWindow title="~/genryz — sudo upgrade">
          <div className="text-center py-6">
            <p className="text-[#8B949E] text-[12px] mb-3" style={{ fontFamily: MONO }}>$ sudo upgrade --package</p>
            <h2 className="text-2xl lg:text-3xl font-extrabold text-[#C9D1D9] mb-3" style={{ fontFamily: MONO }}>
              unlock <span className="text-[#3FB950]">all packages</span>
            </h2>
            <p className="text-[#8B949E] text-sm mb-7 max-w-md mx-auto">
              Everything bundled at one price — courses, mentorship, community, lifetime updates. Save up to 40%.
            </p>
            <button onClick={() => navigate("/package")}
              className="inline-flex items-center gap-2 rounded-lg bg-[#3FB950] text-[#0D1117] px-6 py-3 text-sm font-extrabold transition-all hover:brightness-110 active:scale-95"
              style={{ fontFamily: MONO }}>
              $ view_package <ArrowRight size={15} />
            </button>
          </div>
        </TermWindow>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-[#0D1117]/8 bg-[#FAFBFC]">
        <div className="max-w-6xl mx-auto px-5 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6" style={{ fontFamily: MONO }}>
          <div>
            <p className="text-[#1F9D46] font-bold text-sm">&gt; genryz</p>
            <p className="text-[#6E7681] text-[11px] mt-1">~ exit · India's GenZ income operating system</p>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-[12px] text-[#4A5360]">
            <button onClick={() => navigate("/courses")} className="hover:text-[#1F9D46]">~/courses</button>
            <button onClick={() => navigate("/package")} className="hover:text-[#1F9D46]">~/package</button>
            <button onClick={() => navigate("/about")} className="hover:text-[#1F9D46]">~/about</button>
            <button onClick={() => navigate("/contact")} className="hover:text-[#1F9D46]">~/contact</button>
          </div>
          <p className="text-[#6E7681] text-[11px]">© {new Date().getFullYear()} genryz</p>
        </div>
      </footer>
    </div>
  );
}
